import { useCallback } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FloatInput } from 'ui-components/components/float-input';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import type { LtlBookingDetails } from '@shipstation/client-common/types/ltl';
import { useFreightQuote } from '../../freight-quote-context';

import style from './additional-details-step.module.less';

const additionalDetailsStepMessages = defineMessages({
  pickupReadyTimeLabel: {
    id: 'I1Tinq',
    description: 'Pickup ready time label in additional details step',
    defaultMessage: 'Pickup Ready Time',
  },
  pickupCloseTimeLabel: {
    id: 'h5/uyd',
    description: 'Pickup close time label in additional details step',
    defaultMessage: 'Pickup Close Time',
  },
  selfScheduledLabel: {
    id: 'kbqEU7',
    description: 'Self-scheduled pickup checkbox label in additional details step',
    defaultMessage: 'I will arrange a pickup with the carrier separately',
  },
  pickupInstructionsLabel: {
    id: 'OCXBt6',
    description: 'Pickup instructions label in additional details step',
    defaultMessage: 'Pickup Instructions',
  },
  deliveryInstructionsLabel: {
    id: 'KjxLAQ',
    description: 'Delivery instructions label in additional details step',
    defaultMessage: 'Delivery Instructions',
  },
  handlingInstructionsLabel: {
    id: 'HXI9ZD',
    description: 'Handling instructions label in additional details step',
    defaultMessage: 'Handling Instructions',
  },
  referenceNumberLabel: {
    id: 'cpXgz0',
    description: 'Reference number label in additional details step',
    defaultMessage: 'Reference Number',
  },
  saveButton: {
    id: 'iOIAZO',
    description: 'Save button in additional details step',
    defaultMessage: 'Save',
  },
  collapsedSummaryPickup: {
    id: '6pIyro',
    description: 'Collapsed additional details step summary showing pickup window',
    defaultMessage: 'Pickup: {ready} – {close}',
  },
  collapsedSummaryRef: {
    id: 'SGcmjD',
    description: 'Reference number in collapsed additional details summary',
    defaultMessage: 'Ref: {ref}',
  },
});

type AdditionalDetailsStepProps = {
  onSave: () => void;
};

export const AdditionalDetailsStepSummary = () => {
  const { state } = useFreightQuote();
  const intl = useIntl();
  const booking = state.bookingDetails;

  return (
    <FlexColumn gap={Gap.Space4}>
      {(booking.pickupReadyTime || booking.pickupCloseTime) && (
        <Text
          textType={TextType.Caption}
          value={intl.formatMessage(additionalDetailsStepMessages.collapsedSummaryPickup, {
            ready: booking.pickupReadyTime || '—',
            close: booking.pickupCloseTime || '—',
          })}
        />
      )}
      {booking.referenceNumber && (
        <Text
          textType={TextType.Caption}
          value={intl.formatMessage(additionalDetailsStepMessages.collapsedSummaryRef, {
            ref: booking.referenceNumber,
          })}
        />
      )}
    </FlexColumn>
  );
};

export const AdditionalDetailsStep = ({ onSave }: AdditionalDetailsStepProps) => {
  const intl = useIntl();
  const { state, localDispatch } = useFreightQuote();
  const bookingDetails: LtlBookingDetails = state.bookingDetails;

  const handleField = useCallback(
    (field: keyof LtlBookingDetails, value: string | boolean) => {
      localDispatch({
        type: 'UPDATE_BOOKING_DETAILS',
        bookingDetails: { ...bookingDetails, [field]: value },
      });
    },
    [localDispatch, bookingDetails],
  );

  return (
    <FlexColumn gap={Gap.Space16}>
      <FlexRow gap={Gap.Space12}>
        <div className={style['time-field']}>
          <FloatInput
            label={intl.formatMessage(additionalDetailsStepMessages.pickupReadyTimeLabel)}
            onChange={(value: string) => handleField('pickupReadyTime', value)}
            type='time'
            value={bookingDetails.pickupReadyTime}
          />
        </div>
        <div className={style['time-field']}>
          <FloatInput
            label={intl.formatMessage(additionalDetailsStepMessages.pickupCloseTimeLabel)}
            onChange={(value: string) => handleField('pickupCloseTime', value)}
            type='time'
            value={bookingDetails.pickupCloseTime}
          />
        </div>
      </FlexRow>

      <Checkbox
        checked={bookingDetails.isSelfScheduled}
        label={intl.formatMessage(additionalDetailsStepMessages.selfScheduledLabel)}
        onChange={(checked: boolean) => handleField('isSelfScheduled', checked)}
      />

      <FloatTextArea
        label={intl.formatMessage(additionalDetailsStepMessages.pickupInstructionsLabel)}
        onChange={(value: string) => handleField('pickupInstructions', value)}
        value={bookingDetails.pickupInstructions ?? ''}
      />

      <FloatTextArea
        label={intl.formatMessage(additionalDetailsStepMessages.deliveryInstructionsLabel)}
        onChange={(value: string) => handleField('deliveryInstructions', value)}
        value={bookingDetails.deliveryInstructions ?? ''}
      />

      <FloatTextArea
        label={intl.formatMessage(additionalDetailsStepMessages.handlingInstructionsLabel)}
        onChange={(value: string) => handleField('specialInstructions', value)}
        value={bookingDetails.specialInstructions}
      />

      <FloatInput
        label={intl.formatMessage(additionalDetailsStepMessages.referenceNumberLabel)}
        onChange={(value: string) => handleField('referenceNumber', value)}
        value={bookingDetails.referenceNumber}
      />

      <div className={style['step-actions']}>
        <Button onClick={onSave} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...additionalDetailsStepMessages.saveButton} />
        </Button>
      </div>
    </FlexColumn>
  );
};
